import { type WorkspaceItem } from 'api/workspace/endpoints/getWorkspaceContents';
import { WorkspaceItemLabel } from './WorkspaceItemLabel';
import { WorkspaceItemTooltip } from './WorkspaceItemTooltip';

// One typesafe label component per object type: each hardcodes its navigation
// icon and renders the tooltip with the common fields plus its own extras. The
// icons mirror the vertical nav (`pages.icon`); several types share the generic
// `settings` icon there, as the nav itself does.
type Item<T extends WorkspaceItem['type']> = Extract<WorkspaceItem, { type: T }>;

const ZoneLabel = ({ item }: { item: Item<'zone'> }) => (
  <WorkspaceItemLabel icon="settings" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Zone" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const FolderLabel = ({ item }: { item: Item<'folder'> }) => (
  <WorkspaceItemLabel icon="folder" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Folder" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const NodeDefinitionLabel = ({ item }: { item: Item<'node_definition'> }) => (
  <WorkspaceItemLabel icon="share-2" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Node definition" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName}>
      <tr>
        <td>Node type</td>
        <td>{item.nodeType}</td>
      </tr>
    </WorkspaceItemTooltip>
  </WorkspaceItemLabel>
);

const IntegrationLabel = ({ item }: { item: Item<'integration'> }) => (
  <WorkspaceItemLabel icon="codesandbox" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Integration" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const EventLabel = ({ item }: { item: Item<'event'> }) => (
  <WorkspaceItemLabel icon="share-2" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Event" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName}>
      <tr>
        <td>Event type</td>
        <td>{item.eventType}</td>
      </tr>
    </WorkspaceItemTooltip>
  </WorkspaceItemLabel>
);

const LockLabel = ({ item }: { item: Item<'lock'> }) => (
  <WorkspaceItemLabel icon="settings" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Lock" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const TriggerLabel = ({ item }: { item: Item<'trigger'> }) => (
  <WorkspaceItemLabel icon="zap" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Trigger" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const QueueLabel = ({ item }: { item: Item<'queue'> }) => (
  <WorkspaceItemLabel icon="settings" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Queue" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName}>
      <tr>
        <td>Status</td>
        <td>{item.status}</td>
      </tr>
    </WorkspaceItemTooltip>
  </WorkspaceItemLabel>
);

const SchedulingWindowLabel = ({ item }: { item: Item<'scheduling_window'> }) => (
  <WorkspaceItemLabel icon="calendar" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Scheduling window" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const RecurrenceDefinitionLabel = ({ item }: { item: Item<'recurrence_definition'> }) => (
  <WorkspaceItemLabel icon="repeat" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Recurrence definition" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const ReportDefinitionLabel = ({ item }: { item: Item<'report_definition'> }) => (
  <WorkspaceItemLabel icon="file-text" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Report definition" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const MonitoringDashboardLabel = ({ item }: { item: Item<'monitoring_dashboard'> }) => (
  <WorkspaceItemLabel icon="layout" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Monitoring dashboard" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const PythonLibraryLabel = ({ item }: { item: Item<'python_library'> }) => (
  <WorkspaceItemLabel icon="code" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Python library" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const TableLabel = ({ item }: { item: Item<'table'> }) => (
  <WorkspaceItemLabel icon="settings" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Table" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName} />
  </WorkspaceItemLabel>
);

const ConnectionLabel = ({ item }: { item: Item<'connection'> }) => (
  <WorkspaceItemLabel icon="settings" name={item.name}>
    <WorkspaceItemTooltip typeLabel="Connection" id={item.id} name={item.name} description={item.description} zoneName={item.zoneName}>
      <tr>
        <td>Connection type</td>
        <td>{item.connectionType}</td>
      </tr>
    </WorkspaceItemTooltip>
  </WorkspaceItemLabel>
);

/**
 * Renders the right per-type label for any workspace item (green name + nav
 * icon + type-specific tooltip). The switch is exhaustive over the WorkspaceItem
 * union — adding a type is a compile error here until it's handled.
 */
export const WorkspaceItemLabelFor = ({ item }: { item: WorkspaceItem }) => {
  switch (item.type) {
    case 'zone':
      return <ZoneLabel item={item} />;
    case 'folder':
      return <FolderLabel item={item} />;
    case 'node_definition':
      return <NodeDefinitionLabel item={item} />;
    case 'integration':
      return <IntegrationLabel item={item} />;
    case 'event':
      return <EventLabel item={item} />;
    case 'lock':
      return <LockLabel item={item} />;
    case 'trigger':
      return <TriggerLabel item={item} />;
    case 'queue':
      return <QueueLabel item={item} />;
    case 'scheduling_window':
      return <SchedulingWindowLabel item={item} />;
    case 'recurrence_definition':
      return <RecurrenceDefinitionLabel item={item} />;
    case 'report_definition':
      return <ReportDefinitionLabel item={item} />;
    case 'monitoring_dashboard':
      return <MonitoringDashboardLabel item={item} />;
    case 'python_library':
      return <PythonLibraryLabel item={item} />;
    case 'table':
      return <TableLabel item={item} />;
    case 'connection':
      return <ConnectionLabel item={item} />;
    default: {
      const _exhaustive: never = item;
      return _exhaustive;
    }
  }
};
